import { C } from '../styles';
import BlogLayout, { P, H2b, H3b } from './BlogLayout';
import { posts } from './posts';
import Photo from '../components/Photo';
import { PHOTOS } from '../content/photos';

const meta = posts.find((p) => p.slug === 'what-we-got-wrong-in-month-one');

// The month-one ad numbers, exactly as they read in the account.
//
// EVERY FIGURE ON THIS PAGE IS REAL AND WAS CHECKED BEFORE PUBLISHING. The entire
// value of this post is that it is not a growth-story fabrication — one invented
// number would spend that for nothing. If any of it stops being true, correct the
// post rather than leaving it up. That applies to the prose as much as the table:
// it states that the fee is gone and that there are fourteen photographs on the site.
//
// The load-time paragraph deliberately claims no figure. An earlier draft said "about
// a second" had come off; that could not be substantiated — PSI's API was quota-blocked,
// and the photo release measurably put ~0.48s of mobile LCP back — so it was rewritten
// to describe the direction and the trade-off instead. Do not reintroduce a number
// there unless you have a like-for-like measurement to back it.
//
// Note for anyone re-checking the arithmetic: the 3.4% CTR is Meta's, computed from
// *all* clicks (482) against impressions, not from the 176 link clicks. 176/14,175
// would be 1.24%. Both numbers are correct and they measure different things — which
// is why the row is labelled "(all clicks)" rather than leaving a reader to divide.
const funnel = [
  { k: 'Impressions', v: '14,175' },
  { k: 'People reached', v: '5,860' },
  { k: 'Click-through rate (all clicks)', v: '3.4%' },
  { k: 'Link clicks', v: '176' },
  { k: 'People who actually reached the site', v: '95' },
  { k: 'Leads', v: '1' },
  { k: 'Cost per lead', v: '$235' },
];

function FunnelRow({ k, v, emphasis }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto', gap: 16,
      padding: '13px 0', borderBottom: `1px solid ${C.bdr}`, alignItems: 'baseline',
    }}>
      <div style={{ fontSize: 14, color: emphasis ? C.txt : C.mut, fontWeight: emphasis ? 600 : 400, lineHeight: 1.5 }}>{k}</div>
      <div style={{
        fontFamily: "'Barlow Condensed', sans-serif", fontSize: 22, fontWeight: 700,
        color: emphasis ? C.orange : C.txt, textAlign: 'right', lineHeight: 1.1,
      }}>{v}</div>
    </div>
  );
}

// Same <picture> pattern as the homepage gallery: WebP with a JPEG fallback, explicit
// width/height, lazy below the fold, alt text verbatim from the kit manifest. The
// caption repeats the alt text, so it is hidden from assistive tech to stop a screen
// reader reading the same description twice.
function Figure({ slug }) {
  return (
    <figure style={{
      margin: '0 0 26px', background: C.surf, border: `1px solid ${C.bdr}`,
      borderRadius: 10, overflow: 'hidden',
    }}>
      <Photo slug={slug} ratio="4 / 3" sizes={'(max-width: 768px) 92vw, 712px'} />
      <figcaption aria-hidden="true" style={{ padding: '12px 16px', fontSize: 12, lineHeight: 1.55, color: C.mut }}>
        {PHOTOS[slug].alt}
      </figcaption>
    </figure>
  );
}

export default function WhatWeGotWrongInMonthOne() {
  return (
    <BlogLayout title={meta.title} description={meta.description} label="Field note">
      <P>We ran ads for MetalMatch for one month. Here is what came back.</P>

      <div style={{ margin: '26px 0 24px' }}>
        {funnel.map((row) => (
          <FunnelRow key={row.k} {...row} emphasis={row.k === 'Leads' || row.k === 'Cost per lead'} />
        ))}
      </div>

      <P>One lead. Two hundred and thirty-five dollars for it.</P>
      <P>
        The instinct when you see a number like that is to blame the advertising. We nearly did. But a
        3.4% click-through rate is good — better than the benchmark for our category. People saw the ad,
        understood the offer, and wanted it enough to click.
      </P>
      <P>Then they arrived at our website and left. About 99% of them.</P>
      <P>
        So the problem was never the ads. It was the thing the ads were pointing at. Here is what was
        wrong with it.
      </P>

      <Figure slug="guardrail-frames-in-production" />

      <H3b>1. We asked for money before we had given anyone anything</H3b>
      <P>
        Our pricing section sat directly above the enquiry form. It said:{' '}
        <strong style={{ color: C.txt }}>project intake, $99–$299 per submission, charged at submission
        regardless of outcome.</strong>
      </P>
      <P>
        Read that the way a contractor reads it. He needs a bracket fabricated. He can ring three shops
        this afternoon for free. We were asking him for up to three hundred dollars for the privilege of
        finding out whether we could help — and telling him, in the same breath, that he would pay it even
        if nothing came of it.
      </P>
      <P>We had built a toll booth in front of a service nobody had tried yet.</P>
      <P>
        The fee is gone. Submitting a project to MetalMatch is free, and it will stay free. We are paid a
        success fee by the fabricator when a job actually closes, which means we only make money when you
        get something built.
      </P>

      <H3b>2. We had no photographs of metal anywhere on the site</H3b>
      <P>
        Not one. We checked the entire site — homepage, blog, every page — and found zero photographs. The
        whole thing was dark gradients, typography, and a mocked-up interface panel showing an example
        project.
      </P>
      <P>It looked, to be fair, quite smart. It also looked like nobody involved had ever been in a workshop.</P>

      <Figure slug="rebar-cage-flatbed-delivery" />

      <P>
        Our customers are people who make things out of steel, or who buy from people who do. They can tell
        the difference between a weld and a picture of a weld from across a room. Showing that audience a
        website with no steel on it is telling them there is no shop behind it.
      </P>
      <P>
        There are now fourteen photographs on this site. Every one is real work, photographed on a shop
        floor by our partner Ian, who has spent thirty-one years in Ontario fabrication and runs a Toronto
        steel supply business. Rebar cages on a flatbed. Column bases with the gussets welded on. Guardrail
        frames mid-run with the grinder still throwing sparks in the background.
      </P>
      <P>
        They are not jobs that came through MetalMatch — we are new, and we are not going to pretend
        otherwise. They are examples of the standard we are holding the network to, photographed by the man
        who vets it.
      </P>

      <H3b>3. Every button said the wrong thing</H3b>
      <P>
        Every call to action on the site read <strong style={{ color: C.txt }}>“Evaluate Quotes.”</strong>
      </P>
      <P>
        Evaluating quotes is something you do at the end, once several shops have priced your job. It is
        not what a visitor wants at the start. At the start they have a part they need made and no idea
        whether this website can make it. We had labelled the front door with the name of a room three
        doors further in.
      </P>
      <P>
        The buttons now say what actually happens. And the form asks what you need made, in plain language,
        before it asks anything about you.
      </P>

      <H2b>The one we nearly missed</H2b>
      <P>
        There was a fourth problem, and we only found it because we went looking for why the first three
        didn’t fully explain the numbers.
      </P>
      <P>
        Our pages took nearly eight seconds to display anything on a mobile connection. Not to finish
        loading — to show the first meaningful thing on screen. Eight seconds, on a phone, for someone who
        has just tapped an ad while standing in a yard.
      </P>
      <P>
        And look again at the funnel above: 176 link clicks, 95 people recorded as reaching the site.
        Eighty-one people are unaccounted for between the tap and the page. Some of that is ordinary
        measurement loss. Some of it is people who arrived, waited, and left before the page had drawn
        itself.
      </P>
      <P>
        We’ve started on it — some render-blocking work is gone — but we’ve also just put fourteen
        photographs on the site, and those cost us back some of what we gained. Making a page faster and
        making it convincing pull against each other, and we haven’t balanced that yet. What we can say is
        that it changed how we read our own numbers. It is entirely possible to build a page that converts
        well and still lose most of your visitors before they ever see it.
      </P>

      <H2b>What we are not going to tell you</H2b>
      <P>
        We are not going to tell you this is fixed and business is booming. It has been a week. One lead is
        one lead.
      </P>
      <P>
        We could have filled this page with testimonials nobody wrote, or a case study about a customer who
        does not exist yet, and you would have had no way of knowing. Plenty of new companies do exactly
        that, and it is the reason a tradesperson’s first instinct on a website like ours is suspicion.
      </P>
      <P>
        What we can do instead is show you the actual work our network produces, tell you what things
        actually cost, and publish the numbers when they are bad as well as when they are good.
      </P>
      <P>
        If you need custom metal fabricated in Ontario — a one-off part, a production run, or a drawing you
        need somebody to actually build — it costs nothing to send it to us. A rough description is enough.
        A sketch helps but isn’t required.
      </P>
      <P>We will post the next month’s numbers whether they are better or not.</P>
    </BlogLayout>
  );
}
